import type { SegmenterElements } from "./segmenter-elements.js";
import { appendProOffer } from "../site/pro-offer-link.js";

/**
 * The two parts of the page that belong to saving rather than to the list.
 *
 * The name a reader gives the text, and the line that explains a save the
 * route refused. Apart from `segmenter-view.ts`, which draws the words, and
 * apart from `segmenter-saved-words.ts`, which decides what a press means.
 * This is what those two write to.
 */
export class SegmenterSaving {
  readonly #elements: SegmenterElements;

  constructor(elements: SegmenterElements) {
    this.#elements = elements;
  }

  /**
   * The tag typed for this text, or nothing where the reader typed none.
   *
   * **The name is optional.** It used to be `required`, which put a form in
   * front of the one press the word list exists for: a reader who wanted 学习
   * on their list had first to name the article they found it in. A word saved
   * with no name is an untagged word, which is what saving one from a 词典
   * entry has always given.
   */
  tag(): string | undefined {
    const tag = this.#elements.tag.value.trim();

    return tag === "" ? undefined : tag;
  }

  /**
   * Ask the browser to explain a tag it will not accept.
   *
   * The field still carries `maxlength`, so this is no longer about a missing
   * name and is still worth asking.
   */
  validTag(): boolean {
    return this.#elements.tag.reportValidity();
  }

  /**
   * Explain why a free account could not save another word, and offer Pro
   * 专业 in the words every offer takes from `src/user/pro-offer.ts`.
   */
  showFullList(): void {
    const status = this.#elements.saveStatus;

    status.textContent =
      "You've reached your saved word limit. Remove a saved word to save another.";
    appendProOffer(status, "segmenter", "saved-words");
    status.hidden = false;
  }

  /** Clear a previous save message after a press succeeds. */
  clearStatus(): void {
    this.#elements.saveStatus.textContent = "";
    this.#elements.saveStatus.hidden = true;
  }
}
